import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Mail, MessageCircle, Copy, Check, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Reusable Share dropdown for any document (Invoice, PO, E-way bill, Challan…).
 * Preferred flow: pass `resolveShare` which returns an object
 *   { shortUrl, details: { number, customer_name, vendor_name, amount, date, kind, issuer, status } }
 * and this component composes a rich, named-URL message body:
 *
 *   📄 Invoice #INV-0042 · Acme Ceramics
 *   Amount: ₹1,25,000 · Date: 03 Feb 2026
 *   📎 Open here → https://<host>/s/aBcD12
 *
 * Legacy: keep `url` and `resolveUrl` for older call-sites (they'll share the raw URL).
 *
 * Props:
 *   docLabel:      string   — human title used in dropdown header (e.g. "Invoice INV-0042").
 *   url?:          string   — static direct URL (legacy).
 *   resolveUrl?:   () => Promise<string|null>  — async raw-URL resolver (legacy).
 *   resolveShare?: () => Promise<{shortUrl, details}|null>  — preferred rich resolver.
 *   variant/size/testId — button styling.
 */
export default function ShareButton({
  docLabel = "Document",
  url,
  resolveUrl,
  resolveShare,
  variant = "outline",
  size = "sm",
  testId = "share-doc",
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [resolving, setResolving] = useState(false);

  const fmtAmount = (n) => {
    if (n == null) return null;
    try { return `₹${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`; }
    catch { return String(n); }
  };
  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  /**
   * Build a rich, named message for WhatsApp/Email/Copy.
   * Returns { url, message } — the URL is what the recipient clicks, message is what goes into the body.
   */
  const buildShare = async () => {
    if (resolveShare) {
      setResolving(true);
      try {
        const payload = await resolveShare();
        if (!payload) return null;
        const { shortUrl, details } = payload;
        return { url: shortUrl, message: composeMessage(shortUrl, details) };
      } finally { setResolving(false); }
    }
    // Legacy path — raw URL
    const raw = url || (resolveUrl ? await (async () => { setResolving(true); try { return await resolveUrl(); } finally { setResolving(false); } })() : null);
    if (!raw) return null;
    return { url: raw, message: `${docLabel}\nOpen: ${raw}` };
  };

  const composeMessage = (shortUrl, d = {}) => {
    const kindLabel = ({
      invoice: "Invoice", eway: "E-way bill", challan: "Delivery challan", po: "Purchase order",
    }[d.kind] || d.kind || "Document").replace(/\b\w/g, (c) => c.toUpperCase());
    const number = d.number ? `#${d.number}` : "";
    const party = d.customer_name || d.vendor_name || "";
    const amount = fmtAmount(d.amount);
    const date = fmtDate(d.date);
    const issuer = d.issuer;

    // Compose message
    const lines = [];
    lines.push(`📄 ${kindLabel} ${number}${party ? ` · ${party}` : ""}`.trim());
    if (issuer && !party) lines.push(`From: ${issuer}`);
    const meta = [];
    if (amount) meta.push(`Amount: ${amount}`);
    if (date) meta.push(`Date: ${date}`);
    if (meta.length) lines.push(meta.join(" · "));
    lines.push("");
    lines.push(`📎 Open securely → ${shortUrl}`);
    return lines.join("\n");
  };

  const openMail = async () => {
    const s = await buildShare(); if (!s) { toast.error(t("toasts.failed")); return; }
    const subject = encodeURIComponent(String(docLabel));
    const body = encodeURIComponent(s.message);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const openWhatsApp = async () => {
    const s = await buildShare(); if (!s) { toast.error(t("toasts.failed")); return; }
    const body = encodeURIComponent(s.message);
    window.open(`https://wa.me/?text=${body}`, "_blank", "noopener,noreferrer");
  };
  const copy = async () => {
    const s = await buildShare(); if (!s) { toast.error(t("toasts.failed")); return; }
    try {
      await navigator.clipboard.writeText(s.url);
      setCopied(true);
      toast.success(t("common.linkCopied"));
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error(t("toasts.failed"));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} data-testid={`${testId}-trigger`} disabled={resolving}>
          {resolving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />}
          {t("common.share")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{docLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={openWhatsApp} data-testid={`${testId}-whatsapp`} className="cursor-pointer">
          <MessageCircle className="w-4 h-4 mr-2 text-emerald-600" /> {t("common.shareWhatsApp")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openMail} data-testid={`${testId}-email`} className="cursor-pointer">
          <Mail className="w-4 h-4 mr-2 text-blue-600" /> {t("common.shareEmail")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={copy} data-testid={`${testId}-copy`} className="cursor-pointer">
          {copied ? <Check className="w-4 h-4 mr-2 text-emerald-600" /> : <Copy className="w-4 h-4 mr-2" />}
          {t("common.copyLink")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
